<template>
  <div class="flex flex-col h-full content-start space-y-5 grid gap-2">
    <div class="mb-8 pb-8">
      <Icon @click="goBack" icon="ant-design:arrow-left-outlined" class="text-xl cursor-pointer" />

      <h2 class="text-primary-dark font-bold mb-5 mt-5 text-xl">Paragraph Length</h2>
      <h3 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Sentences per paragraph</h3>
      <p>
        Paragraphs represent the construction blocks of any manuscript section, and paragraphs are
        comprised of sentences. If a paragraph is too short, the reader will feel like they are
        missing information to follow the logic of your narrative, and if the paragraph is too long,
        the reader might be unable to clearly follow the importance of the information you are
        presenting. The goal should be to explore and discuss a single topic per paragraph,
        providing just enough detail so that the reader understands the importance of the topic and
        can make logical connections with other topics presented throughout your entire paper.
      </p>
      <p>
        WriteWise has defined an “ideal” number of sentences per paragraph for each section
        (Introduction, Methods, etc.). The ideal sentence range was determined through analysis of
        the gold-standard corpus. Keeping within these ideal ranges will make it easier for the
        reader to understand your message. This is especially important to consider for scientific
        texts. Therefore, you might keep the following in mind:
      </p>
      <ul class="list-disc grid gap-4 flex-1 p-5">
        <li>Use WriteWise to detect paragraphs that do not fit within the ideal range.</li>
        <li>
          When paragraph length does not fit within the ideal range, it is likely that either too
          few or too many ideas are being communicated in the paragraph. This makes it hard for
          readers to follow a clear narrative throughout your paper.
        </li>
        <li>
          Try to separate long paragraphs into two or more paragraphs. The key is to identify where
          a shift in the key topic of the paragraph occurs and to begin a new paragraph at the
          detected shift.
        </li>
        <li>
          Try to join short paragraphs with either the preceeding or subsequent paragraph. The trick
          is to join the short paragraph with information touching on the same general topic.
        </li>
      </ul>
      <br />
      <div class="text-primary-main underline">Example:</div>

      <p>
        <span class="text-primary-main">Original paragraph (13 sentences):</span>
        <br />
        During the initiation of an adaptive immune response, the antigen-presenting cell (APC)
        serves as a point of integration where tissue-derived signals are conveyed to T cells.
        Myeloid APCs, such as macrophages and dendritic cells (DCs), are responsible for the display
        of specific peptides in complex with major histocompatibility complex (MHC) molecules, and
        for the expression of co-signaling factors that tune the T cell response. The expression of
        stimulatory or inhibitory co-signaling molecules depends on the local immune environment and
        activation state of the APC. In particular, interferon gamma (IFNγ) stimulates the surface
        expression of MHC proteins, co-stimulatory proteins such as CD40, and the secretion of
        cytokines like IL-12 and IL-18, to promote T cell activation and the production of
        IFNγ-producing T-helper type 1 (Th1) effector cells. In the context of local inflammation,
        pattern recognition receptor (PRR) ligands and endogenous immune activators can collaborate
        with IFNγ to induce the expression of co-inhibitory molecules, like programmed death-ligand
        1 (PD-L1), which binds T cell programmed death receptor 1 (PD1) to limit immune activation
        and mitigate T cell-mediated tissue damage. IFNγ mediates these complex effects via binding
        to a heterodimeric surface receptor. The subunits of the complex, IFNGR1 and IFNGR2,
        assemble once IFNGR1 is bound by its ligand. Complex assembly promotes the phosphorylation
        of Janus kinases 1 and 2 (JAK1 and JAK2) followed by activation of the signal transducer and
        activation of transcription 1 (STAT1). Phosphorylated STAT1 then dimerizes and translocates
        to the nucleus to activate the transcription of genes containing promoters with
        IFNγ-activated sequences (GAS), which includes other transcription factors such as
        interferon regulatory factor 1 (Irf1) that amplify the expression of a large regulon that
        includes T cell co-signaling molecules. The importance of this signaling pathway is evident
        in a variety of diseases including cancer, autoimmunity, and infection. Individuals with
        inborn deficiencies in IFNγ signaling, including mutations to the receptor, suffer from a
        defect in Th1 immunity that results in an immunodeficiency termed Mendelian susceptibility
        to mycobacterial disease (MSMD). Conversely, antagonists of IFNγ-inducible inhibitory
        molecules, such as PD-L1, are the basis for checkpoint inhibitor therapies that effectively
        promote T cell-mediated tumor immunity. While the obligate components of the IFNγ signaling
        pathway are well known, characterization of additional regulators of this response promises
        to identify both additional causes of immune dysfunction and new therapeutic targets.
      </p><br />
      <p>
        <span class="text-primary-main"
          >Modified paragraphs (Paragraph 1: 5 sentences; Paragraph 2: 8 sentences):</span
        >
        <br /><br />
        During the initiation of an adaptive immune response, the antigen-presenting cell (APC)
        serves as a point of integration where tissue-derived signals are conveyed to T cells.
        Myeloid APCs, such as macrophages and dendritic cells (DCs), are responsible for the display
        of specific peptides in complex with major histocompatibility complex (MHC) molecules, and
        for the expression of co-signaling factors that tune the T cell response. The expression of
        stimulatory or inhibitory co-signaling molecules depends on the local immune environment and
        activation state of the APC. In particular, interferon gamma (IFNγ) stimulates the surface
        expression of MHC proteins, co-stimulatory proteins such as CD40, and the secretion of
        cytokines like IL-12 and IL-18, to promote T cell activation and the production of
        IFNγ-producing T-helper type 1 (Th1) effector cells. In the context of local inflammation,
        pattern recognition receptor (PRR) ligands and endogenous immune activators can collaborate
        with IFNγ to induce the expression of co-inhibitory molecules, like programmed death-ligand
        1 (PD-L1), which binds T cell programmed death receptor 1 (PD1) to limit immune activation
        and mitigate T cell-mediated tissue damage.
        <br /><br />
        IFNγ mediates these complex effects via binding to a heterodimeric surface receptor. The
        subunits of the complex, IFNGR1 and IFNGR2, assemble once IFNGR1 is bound by its ligand.
        Complex assembly promotes the phosphorylation of Janus kinases 1 and 2 (JAK1 and JAK2)
        followed by activation of the signal transducer and activation of transcription 1 (STAT1).
        Phosphorylated STAT1 then dimerizes and translocates to the nucleus to activate the
        transcription of genes containing promoters with IFNγ-activated sequences (GAS), which
        includes other transcription factors such as interferon regulatory factor 1 (Irf1) that
        amplify the expression of a large regulon that includes T cell co-signaling molecules. The
        importance of this signaling pathway is evident in a variety of diseases including cancer,
        autoimmunity, and infection. Individuals with inborn deficiencies in IFNγ signaling,
        including mutations to the receptor, suffer from a defect in Th1 immunity that results in an
        immunodeficiency termed Mendelian susceptibility to mycobacterial disease (MSMD).
        Conversely, antagonists of IFNγ-inducible inhibitory molecules, such as PD-L1, are the basis
        for checkpoint inhibitor therapies that effectively promote T cell-mediated tumor immunity.
        While the obligate components of the IFNγ signaling pathway are well known, characterization
        of additional regulators of this response promises to identify both additional causes of
        immune dysfunction and new therapeutic targets.
        <br /><br />
        <span class="text-primary-main"
          >Note that while both paragraphs discuss the same subject (i.e., the immune response), the
          specific topic of each paragraph is different. Paragraph 1 presents an overview of the
          adaptive immune response, while paragraph 2 more specifically discusses INFy
          signaling.</span
        >
      </p><br />
      <div>------</div>
      <p>
        <span class="underline">Disclaimer:</span> If you believe your paragraph needs to contain
        fewer or more sentences to communicate your desired message, you can simply ignore the alert
        given by WriteWise. Furthermore, if your paragraph length is close to the ideal range
        indicated by WriteWise, it is likely that you do not need to edit the paragraph. For
        example, if WriteWise indicates the ideal range is 4 to 8 sentences and your paragraph
        contains 9 sentences, then your paragraph is probably alright. WriteWise provides a general
        structure as a reference for you to consider, but WriteWise does not give an “absolute
        truth” for how an Abstract or your paper should be written.
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { Icon } from '@iconify/vue';
import { Vue, Options } from 'vue-class-component';

@Options({
  components: {
    Icon
  }
})
export default class ParagraphLengthLearnMore extends Vue {
  public goBack() {
    this.$emit('closeLearnMore');
  }
}
</script>

<style>
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}
</style>
